import React, { Component } from 'react';
import axios from "axios";
import './style.scss';

const API_URL = 'https://script.google.com/macros/s/AKfycbzPJwkaCTf4s4tthevgLI4ErBzMhA3OnI5qg-KQdXXE81ldx1YgvwjAGlBrFUT_oZA9xA/exec';

function parseDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(str) {
  const d = parseDate(str);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function renderRating(rating) {
  switch (rating) {
    case 0: return <span className="rating-bad">❌</span>;
    case 2: return <span className="rating-star">⭐</span>;
    case 3: return <span className="rating-star">⭐⭐</span>;
    default: return <span className="rating-neutral">—</span>;
  }
}

export default class BooksPage extends Component {
  state = {
    books: [],
    loading: true,
    error: false,
    ratingFilter: -1,
    searchQuery: '',
    sortNewestFirst: true,
    expandedYears: new Set(),
    stellarExpanded: false,
  };

  componentDidMount() {
    axios.get(API_URL)
      .then(response => {
        const books = (response.data.books || []).map(b => ({
          ...b,
          _date: b.dateCompleted ? parseDate(b.dateCompleted) : null,
          _year: b.dateCompleted ? parseDate(b.dateCompleted).getFullYear() : null,
        }));
        this.setState({ books, loading: false });
      })
      .catch(() => {
        this.setState({ loading: false, error: true });
      });
  }

  toggleYear = (year) => {
    this.setState(prev => {
      const expanded = new Set(prev.expandedYears);
      if (expanded.has(year)) {
        expanded.delete(year);
      } else {
        expanded.add(year);
      }
      return { expandedYears: expanded };
    });
  };

  setRatingFilter = (rating) => {
    this.setState({ ratingFilter: rating });
  };

  setSearchQuery = (e) => {
    this.setState({ searchQuery: e.target.value });
  };

  clearSearch = () => {
    this.setState({ searchQuery: '' });
  };

  toggleStellar = () => {
    this.setState(prev => ({ stellarExpanded: !prev.stellarExpanded }));
  };

  toggleSort = () => {
    this.setState(prev => ({ sortNewestFirst: !prev.sortNewestFirst }));
  };

  render() {
    const { books, loading, error, ratingFilter, searchQuery, sortNewestFirst, expandedYears, stellarExpanded } = this.state;

    if (loading) {
      return (
        <div className="booksContainer">
          <div className="books-loading">Loading your books…</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="booksContainer">
          <div className="books-error">
            <p>Couldn't load book data. Try refreshing the page.</p>
          </div>
        </div>
      );
    }

    const filtered = books.filter(b => {
      if (ratingFilter !== -1 && b.rating !== ratingFilter) return false;
      if (searchQuery && !b.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const da = a._date || new Date(0);
      const db = b._date || new Date(0);
      return sortNewestFirst ? db - da : da - db;
    });

    const yearGroups = {};
    sorted.forEach(b => {
      const y = b._year || 'Unknown';
      if (!yearGroups[y]) yearGroups[y] = [];
      yearGroups[y].push(b);
    });

    const yearKeys = Object.keys(yearGroups).sort((a, b) =>
      sortNewestFirst
        ? (b === 'Unknown' ? -1 : a === 'Unknown' ? 1 : b - a)
        : (a === 'Unknown' ? 1 : b === 'Unknown' ? -1 : a - b)
    );

    const currentYear = new Date().getFullYear();
    const totalBooks = books.length;
    const booksThisYear = books.filter(b => b._year === currentYear).length;
    const stellarBooks = books.filter(b => b.rating === 3);

    const ratingOptions = [
      { value: -1, label: 'All' },
      { value: 0, label: '❌' },
      { value: 1, label: '—' },
      { value: 2, label: '⭐' },
      { value: 3, label: '⭐⭐' },
    ];

    return (
      <div className="booksContainer">
        <h1>Books</h1>

        <div className="books-stats">
          <div className="books-stat">
            <div className="books-stat-value">{totalBooks}</div>
            <div className="books-stat-label">Total books</div>
          </div>
          <div className="books-stat">
            <div className="books-stat-value">{booksThisYear}</div>
            <div className="books-stat-label">Books in {currentYear}</div>
          </div>
        </div>

        {stellarBooks.length > 0 && (
          <div className="books-stellar">
            <div className={`books-stellar-header${stellarExpanded ? '' : ' collapsed'}`} onClick={this.toggleStellar}>
              <span className="books-chevron">▼</span>
              <h2 className="books-stellar-title">⭐⭐ The Essentials</h2>
              <span className="books-stellar-count">{stellarBooks.length} books</span>
            </div>
            {stellarExpanded && (
              <div>
                <p className="books-stellar-subtitle">The 11-out-of-10 books. If you read nothing else, read these.</p>
                <div className="books-stellar-list">
                  {stellarBooks.map((b, i) => {
                    const Tag = b.link ? 'a' : 'div';
                    const linkProps = b.link ? { href: b.link, target: '_blank', rel: 'noopener noreferrer' } : {};
                    return (
                      <Tag className="books-stellar-item" key={i} {...linkProps}>
                        <span className="books-stellar-name">{b.name}</span>
                        {b.note && <span className="books-stellar-note">{b.note}</span>}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="books-controls">
          <div className="books-search-wrapper">
            <input
              type="text"
              className="books-search"
              placeholder="Search all books…"
              value={searchQuery}
              onChange={this.setSearchQuery}
            />
            {searchQuery && (
              <button className="books-search-clear" onClick={this.clearSearch}>&times;</button>
            )}
          </div>
          <div className="books-filters">
            {ratingOptions.map(opt => (
              <button
                key={opt.value}
                className={`books-filter-btn${ratingFilter === opt.value ? ' active' : ''}`}
                onClick={() => this.setRatingFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="books-sort-btn" onClick={this.toggleSort}>
            {sortNewestFirst ? 'Newest first ↓' : 'Oldest first ↑'}
          </button>
        </div>

        {sorted.length === 0 ? (
          <div className="books-empty">No books match your filters.</div>
        ) : (
          yearKeys.map(year => {
            const yearBooks = yearGroups[year];
            const isExpanded = expandedYears.has(year);
            return (
              <div className="books-year-section" key={year}>
                <div
                  className={`books-year-header${isExpanded ? '' : ' collapsed'}`}
                  onClick={() => this.toggleYear(year)}
                >
                  <span className="books-chevron">▼</span>
                  <span className="books-year-title">{year}</span>
                  <span className="books-year-count">
                    {yearBooks.length} book{yearBooks.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {isExpanded && (
                  <div className="books-year-list">
                    {yearBooks.map((b, i) => {
                      const Tag = b.link ? 'a' : 'div';
                      const linkProps = b.link ? { href: b.link, target: '_blank', rel: 'noopener noreferrer' } : {};
                      return (
                        <Tag className="books-card" key={i} {...linkProps}>
                          <div className="books-card-info">
                            <div className="books-card-name">{b.name}</div>
                            {b.dateCompleted && (
                              <div className="books-card-date">{formatDate(b.dateCompleted)}</div>
                            )}
                            {b.note && (
                              <div className="books-card-note">{b.note}</div>
                            )}
                          </div>
                          <div className="books-card-rating">{renderRating(b.rating)}</div>
                        </Tag>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  }
}
